import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Frontend mirror of the tenant's plan entitlements, fetched from the backend
// (GET /api/entitlements/me). The backend remains the sole security boundary —
// this state only drives visibility/UX, never authorization.
const EntitlementContext = createContext(null);

const EMPTY = { loading: true, features: [], planId: null, status: null, userLimit: null, activeUsers: 0, unrestricted: false };

export function EntitlementProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (user === null) return; // auth still resolving
    if (user === false) { setState({ ...EMPTY, loading: false }); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/entitlements/me");
        if (cancelled) return;
        setState({
          loading: false,
          features: data.features || [],
          planId: data.plan_id || null,
          status: data.status || null,
          userLimit: data.user_limit ?? null,
          activeUsers: data.active_users ?? 0,
          unrestricted: !!data.unrestricted,
        });
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const hasFeature = useCallback(
    (key) => {
      if (!key) return true;
      if (state.loading) return true; // avoid a wrong "locked" flash; backend enforces
      return state.unrestricted || state.features.includes(key);
    },
    [state]
  );

  const value = useMemo(() => ({ ...state, hasFeature }), [state, hasFeature]);
  return <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>;
}

export function useEntitlements() {
  const ctx = useContext(EntitlementContext);
  if (!ctx) throw new Error("useEntitlements must be used within EntitlementProvider");
  return ctx;
}
